import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Button, Input, Spinner } from "../components/ui";
import { WorkStatusPill, ProgressBar, QuadrantBadge } from "../components/work";
import ProjectFormModal from "../components/ProjectFormModal";
import BoardView from "../components/BoardView";
import { ArrowLeft, Plus, FolderKanban, Pencil, Trash2, Check, X, Flag, Link2, Calendar, User, Target, LayoutGrid } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2, 10);

function fmtDate(d, lang) {
  if (!d) return "—";
  const [y, m, day] = String(d).split("-").map(Number);
  if (!y || !m || !day) return d;
  return new Date(y, m - 1, day).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US",
    { year: "numeric", month: "short", day: "numeric" });
}

export default function ProjectPage() {
  const { key, spaceId, projectId } = useParams();
  const navigate = useNavigate();
  const { t, lang, entities } = useApp();
  const entity = entities.find((e) => e.key === key);
  const color = entity?.color || "#2563EB";

  const [project, setProject] = useState(null);
  const [parentGoal, setParentGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);

  const [mName, setMName] = useState("");
  const [mDate, setMDate] = useState("");
  const [rForm, setRForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/space-projects/${projectId}`);
      setProject(data);
      if (data.parent_goal_id) {
        try { const g = await api.get(`/goals/${data.parent_goal_id}`); setParentGoal(g.data); }
        catch { setParentGoal(null); }
      } else setParentGoal(null);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => { await api.put(`/space-projects/${projectId}`, fields); await load(); };

  const addMilestone = async () => {
    if (!mName.trim()) return;
    const milestones = [...(project.milestones || []), { id: uid(), name: mName.trim(), target_date: mDate || "", status: "pending" }];
    setMName(""); setMDate("");
    await patch({ milestones });
  };
  const toggleMilestone = async (id) => patch({
    milestones: (project.milestones || []).map((m) => m.id === id ? { ...m, status: m.status === "done" ? "pending" : "done" } : m),
  });
  const setMilestoneDate = async (id, date) => patch({
    milestones: (project.milestones || []).map((m) => m.id === id ? { ...m, target_date: date } : m),
  });
  const removeMilestone = async (id) => patch({
    milestones: (project.milestones || []).filter((m) => m.id !== id),
  });

  const addResource = async () => {
    if (!rForm?.label?.trim() || !rForm?.url?.trim()) return;
    const resources = [...(project.resources || []), { id: uid(), label: rForm.label.trim(), url: rForm.url.trim() }];
    setRForm(null);
    await patch({ resources });
  };
  const removeResource = async (id) => patch({ resources: (project.resources || []).filter((r) => r.id !== id) });

  const deleteProject = async () => {
    if (!window.confirm(lang === "fr" ? "Supprimer ce projet ?" : "Delete this project?")) return;
    await api.delete(`/space-projects/${projectId}`);
    navigate(`/entity/${key}/space/${spaceId}`);
  };

  if (!entity) return <div className="p-8 text-text-secondary">…</div>;
  if (loading || !project) return <div className="py-24 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;

  const backPath = project.parent_goal_id
    ? `/entity/${key}/space/${spaceId}/goal/${project.parent_goal_id}`
    : `/entity/${key}/space/${spaceId}`;

  return (
    <div className="p-6 lg:p-8" data-testid="project-page">
      <div className="max-w-[1400px] mx-auto">
      <button onClick={() => navigate(backPath)} className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors" data-testid="project-back">
        <ArrowLeft className="w-4 h-4" /> {t("back")}
      </button>

      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <FolderKanban className="w-6 h-6" style={{ color }} />
          <h1 className="text-3xl font-heading font-medium tracking-tight">{project.name}</h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowEdit(true)} data-testid="project-edit-button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /> {t("edit")}</button>
          <button onClick={deleteProject} data-testid="project-delete-button" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-red-400 hover:bg-red-500/15 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {project.description && <p className="text-text-secondary text-sm mb-6">{project.description}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 space-y-7">
          {/* Milestones (kept; will feed the Gantt view later) */}
          <section data-testid="project-milestones">
            <div className="flex items-center gap-2 mb-3"><Flag className="w-4 h-4 text-text-secondary" /><h3 className="font-heading font-medium">{t("milestones")}</h3></div>
            <div className="flex items-center gap-2 mb-4">
              <Input placeholder={t("milestone_name")} value={mName} onChange={(e) => setMName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMilestone()} data-testid="milestone-input" />
              <Input type="date" className="w-44" value={mDate} onChange={(e) => setMDate(e.target.value)} data-testid="milestone-date" />
              <Button size="sm" variant="outline" onClick={addMilestone} data-testid="milestone-add"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="space-y-2">
              {(project.milestones || []).map((m) => (
                <Card key={m.id} className="p-3.5" data-testid={`milestone-${m.id}`}>
                  <div className="flex items-center gap-2.5 group">
                    <button onClick={() => toggleMilestone(m.id)} data-testid={`milestone-toggle-${m.id}`}
                      className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0"
                      style={m.status === "done" ? { backgroundColor: color, borderColor: color } : { borderColor: "var(--border-strong)" }}>
                      {m.status === "done" && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <span className={`font-medium flex-1 ${m.status === "done" ? "line-through text-text-secondary" : ""}`}>{m.name}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary">
                      <Calendar className="w-3.5 h-3.5" />
                      <input type="date" value={m.target_date || ""} onChange={(e) => setMilestoneDate(m.id, e.target.value)} data-testid={`milestone-datefield-${m.id}`}
                        className="bg-transparent text-text-secondary text-xs outline-none cursor-pointer hover:text-text-primary" />
                    </span>
                    <button onClick={() => removeMilestone(m.id)} className="opacity-0 group-hover:opacity-100 text-text-secondary hover:text-red-400 transition-all"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </Card>
              ))}
              {(project.milestones || []).length === 0 && <p className="text-sm text-text-secondary">{t("no_milestones")}</p>}
            </div>
          </section>

          {/* Resources */}
          <section data-testid="project-resources">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Link2 className="w-4 h-4 text-text-secondary" /><h3 className="font-heading font-medium">{t("resources")}</h3></div>
              {!rForm && <Button size="sm" variant="ghost" onClick={() => setRForm({})} data-testid="add-resource-button"><Plus className="w-3.5 h-3.5" /> {t("add_resource")}</Button>}
            </div>
            <div className="space-y-2">
              {(project.resources || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border bg-surface group" data-testid={`resource-${r.id}`}>
                  <Link2 className="w-4 h-4 text-text-secondary shrink-0" />
                  <a href={r.url} target="_blank" rel="noreferrer" className="text-sm flex-1 truncate hover:underline" style={{ color }}>{r.label}</a>
                  <button onClick={() => removeResource(r.id)} className="opacity-0 group-hover:opacity-100 text-text-secondary hover:text-red-400 transition-all"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {rForm && (
                <Card className="p-3 space-y-2" data-testid="resource-form">
                  <Input placeholder={t("resource_label")} value={rForm.label || ""} onChange={(e) => setRForm({ ...rForm, label: e.target.value })} data-testid="resource-label" autoFocus />
                  <Input placeholder="https://…" value={rForm.url || ""} onChange={(e) => setRForm({ ...rForm, url: e.target.value })} data-testid="resource-url" />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setRForm(null)}>{t("cancel")}</Button>
                    <Button size="sm" onClick={addResource} data-testid="resource-add">{t("add")}</Button>
                  </div>
                </Card>
              )}
              {(project.resources || []).length === 0 && !rForm && <p className="text-sm text-text-secondary">{t("no_resources")}</p>}
            </div>
          </section>
        </div>

        {/* sidebar */}
        <aside className="space-y-5">
          <Card className="p-5 space-y-4">
            <div><div className="text-xs text-text-secondary mb-1.5">{t("status")}</div><WorkStatusPill status={project.status} lang={lang} /></div>
            <div><div className="text-xs text-text-secondary mb-1.5">{t("priority")}</div>{project.quadrant ? <QuadrantBadge quadrant={project.quadrant} t={t} /> : <span className="text-sm text-text-secondary">—</span>}</div>
            <div><div className="text-xs text-text-secondary mb-1.5">{t("progress")}</div><ProgressBar value={project.progress} color={color} /></div>
            <div className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-text-secondary" /><span className="text-text-secondary">{t("due_date")}:</span> {fmtDate(project.due_date, lang)}</div>
            <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-text-secondary" /><span className="text-text-secondary">{t("champion")}:</span> {project.champion || "—"}</div>
            {parentGoal && (
              <div>
                <div className="text-xs text-text-secondary mb-1.5">{t("parent_goal")}</div>
                <button onClick={() => navigate(`/entity/${key}/space/${spaceId}/goal/${parentGoal.id}`)} data-testid="project-parent-goal" className="inline-flex items-center gap-1.5 text-sm hover:underline"><Target className="w-3.5 h-3.5 text-text-secondary" /> {parentGoal.name}</button>
              </div>
            )}
          </Card>
        </aside>
      </div>
      </div>

      {/* The Board — primary execution surface for this project */}
      <section className="mb-6" data-testid="project-board">
        <div className="flex items-center gap-2 mb-4">
          <LayoutGrid className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-medium">{t("board")}</h3>
        </div>
        <BoardView projectId={projectId} color={color} milestones={project.milestones || []} />
      </section>

      <ProjectFormModal open={showEdit} onClose={() => setShowEdit(false)} onSaved={load} editing={project} entityId={entity.id} spaceId={spaceId} />
    </div>
  );
}
